import { useState, type FormEvent } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { FileText, Plus, Search } from "lucide-react"
import { toast } from "sonner"
import { FIELD_TYPE_LABELS, FieldType, type FieldDefinition } from "@vienna/contracts"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { DataTable, Field, Heading, Hint, Pick } from "@/components/workspace/primitives"
import { api, ApiError } from "@/lib/api"
import { fieldsQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/settings/fields")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(fieldsQuery(params.workspace)),
  component: ContactFields,
})

function keyFrom(label: string) {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^[0-9]/, "f$&")
}

/** Screen 45. */
function ContactFields() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const queryClient = useQueryClient()
  const fields = useSuspenseQuery(fieldsQuery(workspace.slug)).data
  const [open, setOpen] = useState(false)
  const [label, setLabel] = useState("Preferred session")
  const [key, setKey] = useState("preferred_session")
  const [keyTouched, setKeyTouched] = useState(false)
  const [type, setType] = useState<FieldType>("single_select")
  const [options, setOptions] = useState("Portrait, Family, Brand, Event")
  const [query, setQuery] = useState("")
  const create = useMutation({
    mutationFn: () => api<FieldDefinition>(`/w/${workspace.slug}/contacts/fields`, { method: "POST", body: JSON.stringify({ label: label.trim(), key, type, options: type === "single_select" ? options.split(",").map((o) => o.trim()).filter(Boolean) : [] }) }),
    onSuccess: async (field) => {
      await queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug, "fields"] })
      setOpen(false)
      toast.success(`Contact field ${field.label} created`)
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not create the field"),
  })
  function submit(e: FormEvent) {
    e.preventDefault()
    if (!/^[a-z][a-z0-9_]*$/.test(key)) return toast.error("Use a lowercase key with letters, numbers and underscores")
    if (fields.some((f) => f.key === key)) return toast.error("A field already uses this key")
    create.mutate()
  }
  const visible = fields.filter((f) => `${f.label} ${f.key} ${FIELD_TYPE_LABELS[f.type]}`.toLowerCase().includes(query.toLowerCase()))
  return (
    <>
      <Heading
        title="Contact fields"
        description="A shared language for your customer details."
        action={
          can("manage_workspace") ? (
            <Button onClick={() => setOpen(true)}>
              <Plus />
              Create field
            </Button>
          ) : undefined
        }
      />
      <div className="ws-toolbar">
        <div className="ws-search">
          <Search size={16} />
          <Input aria-label="Search contact fields" placeholder="Search fields…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <span className="ws-hint">{fields.length} fields</span>
      </div>
      <DataTable
        heads={["Field", "Key", "Type", "Origin"]}
        rowKeys={visible.map((f) => f.key)}
        rows={visible.map((f) => [
          <span className="ws-icon-label">
            <FileText size={15} />
            {f.label}
          </span>,
          <code className="ws-code">{f.key}</code>,
          f.type === "single_select" && f.options.length ? `${FIELD_TYPE_LABELS[f.type]} · ${f.options.join(", ")}` : FIELD_TYPE_LABELS[f.type],
          <Badge variant={f.origin === "standard" ? "secondary" : "outline"}>{f.origin === "standard" ? "Standard" : "Workspace"}</Badge>,
        ])}
      />
      <div className="mt-6">
        <Hint title="Consent is managed separately">Custom fields cannot grant marketing permission. Consent records keep their source, timestamp, purpose and channel.</Hint>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a contact field</DialogTitle>
            <DialogDescription>Use this field in profiles, segments and automations.</DialogDescription>
          </DialogHeader>
          <form className="ws-dialog-form" onSubmit={submit}>
            <Field label="Display name">
              {(id) => (
                <Input
                  id={id}
                  value={label}
                  required
                  onChange={(e) => {
                    setLabel(e.target.value)
                    if (!keyTouched) setKey(keyFrom(e.target.value))
                  }}
                />
              )}
            </Field>
            <Field label="Field key" hint="A stable identifier. Use lowercase letters and underscores.">
              {(id) => (
                <Input
                  id={id}
                  value={key}
                  required
                  onChange={(e) => {
                    setKeyTouched(true)
                    setKey(e.target.value)
                  }}
                />
              )}
            </Field>
            <Pick label="Type" value={type} options={FieldType.options.map((t) => ({ value: t, label: FIELD_TYPE_LABELS[t] }))} onChange={setType} />
            {type === "single_select" && <Field label="Options" hint="Separate each option with a comma.">{(id) => <Input id={id} value={options} required onChange={(e) => setOptions(e.target.value)} />}</Field>}
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                Create field
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
